
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Home, Mail, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

const EmailVerification = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { language, t } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        // Get parameters from URL
        const tokenHash = searchParams.get('token_hash');
        const type = searchParams.get('type');
        const accessToken = searchParams.get('access_token');
        const refreshToken = searchParams.get('refresh_token');

        console.log('🔍 Email verification params:', { 
          hasTokenHash: !!tokenHash, 
          type, 
          hasAccessToken: !!accessToken,
          hasRefreshToken: !!refreshToken
        });

        // Handle token_hash verification (modern flow)
        if (tokenHash && type) {
          console.log('🔑 Using verifyOtp with token_hash');
          const { data, error } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: type as any
          });

          if (error) {
            console.error('❌ OTP verification error:', error);
            setError(error.message || t('verification_failed', 'فشل في التحقق من البريد الإلكتروني', 'Email verification failed'));
            toast.error(t('verification_failed', 'فشل في التحقق من البريد الإلكتروني', 'Email verification failed'));
          } else if (data.user?.email_confirmed_at) {
            console.log('✅ Email verified successfully with OTP');
            setVerified(true);
            toast.success(t('account_verified_success', 'تم التحقق من حسابك بنجاح! 🎉', 'Your account has been verified successfully! 🎉'), {
              duration: 6000,
              description: t('verification_success_desc', 'يمكنك الآن الوصول إلى جميع ميزات التطبيق', 'You can now access all app features')
            });

            // Redirect to main page after verification
            setTimeout(() => {
              navigate('/');
            }, 3000);
          }
        }
        // Handle access_token/refresh_token verification (alternative flow)
        else if (accessToken && refreshToken) {
          console.log('🔑 Using setSession with tokens');
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });

          if (error) {
            console.error('❌ Session set error:', error);
            setError(error.message || t('verification_failed', 'فشل في التحقق من البريد الإلكتروني', 'Email verification failed'));
            toast.error(t('verification_failed', 'فشل في التحقق من البريد الإلكتروني', 'Email verification failed'));
          } else if (data.user?.email_confirmed_at) {
            console.log('✅ Email verified successfully with session');
            setVerified(true);
            toast.success(t('account_verified_success', 'تم التحقق من حسابك بنجاح! 🎉', 'Your account has been verified successfully! 🎉'), {
              duration: 6000,
              description: t('verification_success_desc', 'يمكنك الآن الوصول إلى جميع ميزات التطبيق', 'You can now access all app features')
            });

            // Redirect to main page after verification
            setTimeout(() => {
              navigate('/');
            }, 3000);
          }
        }
        // Check if user is already verified
        else {
          console.log('⚠️ No verification tokens, checking current session...');
          const { data: { session } } = await supabase.auth.getSession();
          
          if (session?.user?.email_confirmed_at) {
            console.log('✅ User is already verified');
            setVerified(true);
            toast.success(t('already_verified', 'حسابك محقق بالفعل! 🎉', 'Your account is already verified! 🎉'));
            
            // Redirect after short delay
            setTimeout(() => {
              navigate('/');
            }, 2000);
          } else {
            console.log('❌ No valid verification data found');
            setError(t('invalid_verification_link', 'رابط التحقق غير صحيح', 'Invalid verification link'));
            toast.error(t('invalid_verification_link', 'رابط التحقق غير صحيح', 'Invalid verification link'));
          }
        }
      } catch (error: any) {
        console.error('💥 Verification exception:', error);
        setError(error.message || t('verification_error', 'خطأ في التحقق', 'Verification error'));
        toast.error(t('verification_error', 'خطأ في التحقق', 'Verification error'));
      } finally {
        setLoading(false);
      }
    };

    verifyEmail();
  }, [searchParams, navigate, t]);

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-secondary/5 ${language === 'ar' ? 'rtl' : 'ltr'}`}>
        <Card className="w-full max-w-md mx-auto shadow-lg">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <RefreshCw className="h-12 w-12 text-primary animate-spin mb-4" />
            <h2 className="text-xl font-semibold mb-2">
              {t('verifying_email', 'جاري التحقق من البريد الإلكتروني...', 'Verifying your email...')}
            </h2>
            <p className="text-sm text-muted-foreground text-center">
              {t('please_wait', 'يرجى الانتظار أثناء التحقق من عنوان بريدك الإلكتروني', 'Please wait while we verify your email address')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-secondary/5 p-4 ${language === 'ar' ? 'rtl' : 'ltr'}`}>
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <Link to="/" className="inline-flex items-center gap-2 mb-6 hover:opacity-80 transition-opacity">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary shadow-sm">
              <Home className="h-5 w-5 text-primary-foreground" />
            </div>
            <span className="text-xl font-bold bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent">
              {t('maskan', 'مسكن', 'Maskan')}
            </span>
          </Link>
        </div>

        <Card className="shadow-lg border-0 bg-white/80 backdrop-blur-sm">
          <CardHeader className="text-center pb-6">
            <div className="mx-auto mb-6 p-3 rounded-full bg-background shadow-inner">
              {verified ? (
                <CheckCircle className="h-12 w-12 text-green-500" />
              ) : (
                <AlertCircle className="h-12 w-12 text-red-500" />
              )}
            </div>
            <CardTitle className="text-2xl font-bold mb-2">
              {verified 
                ? t('verification_success_title', '🎉 تم التحقق بنجاح!', '🎉 Verification Successful!')
                : t('verification_failed_title', '❌ فشل التحقق', '❌ Verification Failed')
              }
            </CardTitle>
            <CardDescription className="text-center px-2">
              {verified
                ? t('verification_complete_desc', 'حسابك نشط الآن ويمكنك الوصول إلى جميع الميزات', 'Your account is now active and you can access all features')
                : (error || t('verification_failed_desc', 'فشل في التحقق من البريد الإلكتروني. يرجى المحاولة مرة أخرى.', 'Failed to verify your email. Please try again.'))
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {verified ? (
              <>
                <Button onClick={() => navigate('/')} className="w-full h-11" size="lg">
                  <Home className="h-4 w-4 mr-2" />
                  {t('continue_to_app', 'الانتقال إلى التطبيق', 'Continue to App')}
                </Button>
                <div className="text-center pt-2">
                  <p className="text-xs text-muted-foreground">
                    {t('auto_redirect', 'سيتم توجيهك تلقائياً خلال ثوانٍ قليلة...', 'You will be automatically redirected in a few seconds...')}
                  </p>
                </div>
              </>
            ) : (
              <div className="space-y-3">
                <Button onClick={() => navigate('/auth')} className="w-full h-11" size="lg">
                  <Mail className="h-4 w-4 mr-2" />
                  {t('request_new_verification', 'طلب رسالة تحقق جديدة', 'Request New Verification')}
                </Button>
                <Button onClick={() => navigate('/')} variant="outline" className="w-full h-11" size="lg">
                  <Home className="h-4 w-4 mr-2" />
                  {t('back_to_home', 'العودة للرئيسية', 'Back to Home')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EmailVerification;
